import {
  Box,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { LOCALE_CATALOGUE, localeMeta } from "@shared/i18n";
import type { PremiseConfig } from "@shared/types/foyer";

interface Props {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}

/**
 * Chip-based picker for the premise's supported locales + default
 * locale. Every multilingual text input downstream reads from this
 * list, so adding/removing here propagates to the editors.
 */
export default function SupportedLocalesCard({ premise, onChange }: Props) {
  const supported = premise.supportedLocales ?? [];
  const defaultLocale = premise.defaultLocale ?? supported[0] ?? "en";

  function toggle(code: string) {
    const present = supported.includes(code);
    let next: string[];
    if (present) {
      next = supported.filter((c) => c !== code);
    } else {
      next = [...supported, code];
    }
    // Always include the default locale - if you just removed it,
    // promote the next surviving locale (or fall back to "en").
    let nextDefault = defaultLocale;
    if (!next.includes(defaultLocale)) {
      nextDefault = next[0] ?? "en";
      if (!next.length) next = [nextDefault];
    }
    onChange({
      ...premise,
      supportedLocales: next,
      defaultLocale: nextDefault,
    });
  }

  function setDefault(code: string) {
    const next = supported.includes(code) ? supported : [...supported, code];
    onChange({
      ...premise,
      supportedLocales: next,
      defaultLocale: code,
    });
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1">Supported languages</Typography>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Enabled
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {LOCALE_CATALOGUE.map((loc) => {
                const on = supported.includes(loc.code);
                return (
                  <Chip
                    key={loc.code}
                    label={`${loc.code.toUpperCase()} · ${loc.native}`}
                    onClick={() => toggle(loc.code)}
                    color={on ? "primary" : "default"}
                    variant={on ? "filled" : "outlined"}
                    size="small"
                    data-testid={`locale-chip-${loc.code}`}
                  />
                );
              })}
            </Stack>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Default language
            </Typography>
            <Select
              size="small"
              value={defaultLocale}
              onChange={(e) => setDefault(String(e.target.value))}
              sx={{ minWidth: 220 }}
              inputProps={{ "data-testid": "locale-default" }}
            >
              {(supported.length > 0 ? supported : [defaultLocale]).map(
                (code) => {
                  const m = localeMeta(code);
                  return (
                    <MenuItem key={code} value={code}>
                      {m.code.toUpperCase()} · {m.native}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
